import React from 'react';
import { SignIn } from '@clerk/clerk-react';
import { Box, Typography, Grid, Link, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import RoxburghBackground from '../assets/images/roxburgh-background.jpg';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentYear = new Date().getFullYear();

  const getCustomTheme = () => {
    return {
      elements: {
        card: {
          boxShadow: 'none',
          border: 'none',
          padding: '5px',
          width: '100%',
          background: 'transparent',
        },
        cardBox: {
          boxShadow: 'none',
          border: 'none',
          borderRadius: 0,
          width: '100%',
        },
        rootBox: {
          width: '100%',
        },
        formButtonPrimary: {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
          padding: '12px 18px',
          height: '48px',
          fontSize: '1rem',
          borderRadius: '8px',
          fontWeight: '600',
          textTransform: 'none',
          boxShadow: 'none',
        },
        formFieldInput: {
          height: '48px',
          borderRadius: '8px',
          padding: '12px 16px',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          fontSize: '1rem',
          '&:focus': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
          }
        },
        formFieldLabelRow: {
          marginBottom: '8px',
        },
        formFieldLabel: {
          fontWeight: 500,
          fontSize: '0.875rem',
          color: theme.palette.text.primary,
        },
        headerTitle: { display: 'none' },
        headerSubtitle: { display: 'none' },
        footer: { display: 'none' },
        socialButtonsBlockButton: {
          height: '48px',
          borderRadius: '8px',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          fontSize: '0.9rem',
          fontWeight: 500,
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.grey[300],
          }
        },
        dividerLine: {
          background: theme.palette.divider,
        },
        dividerText: {
          color: theme.palette.text.secondary,
          fontWeight: 500,
          fontSize: '0.85rem',
        },
      },
    };
  };

  return (
    <Grid container sx={{ height: '100vh', overflow: 'hidden', bgcolor: 'background.paper' }}>
      {/* Left Side - Login Form */}
      <Grid
        item
        xs={12}
        md={5}
        lg={5}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: { xs: 4, md: 6, lg: 8 },
          height: '100%',
        }}
      >

        {/* Form Container */}
        <Box sx={{ width: '100%', maxWidth: 380, mx: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="displayXS" component="h1" sx={{ mb: 1, color: 'text.primary' }}>
              {t('authentication.signIn')}
            </Typography>
            <Typography variant="textMD" sx={{ color: 'text.secondary' }}>
              {t('authentication.welcomeBack')}
            </Typography>
          </Box>

          <Box sx={{ width: '100%' }}>
            <SignIn 
              routing="hash" 
              forceRedirectUrl="/" 
              appearance={getCustomTheme()}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Typography variant="textSM" sx={{ color: 'text.secondary' }}>
            {t('authentication.copyright', { year: currentYear })}
          </Typography>
          <Link href="#" underline="hover" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            {t('authentication.privacyPolicy')}
          </Link>
          <Link href="mailto:help@roxburgh.co.uk" underline="hover" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            {t('authentication.help')}
          </Link>
        </Box>
      </Grid>

      {/* Right Side - Visual/Animation */}
      {!isMobile && (
        <Grid
          item
          md={7}
          lg={7}
          sx={{
            height: '100%',
            display: 'flex',
          }}
        >
          <Box
            sx={{
              flex: 1,
              borderTopLeftRadius: 80,
              borderBottomLeftRadius: 80,
              overflow: 'hidden',
              position: 'relative',
              backgroundImage: `url(${RoxburghBackground})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              boxShadow: '-20px 0 40px rgba(0,0,0,0.1)',
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default Login;
